import * as React from "react"

import { cn } from "@/lib/utils"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { responsive?: boolean }
>(({ className, responsive = true, ...props }, ref) => (
  <div className={cn(
    "relative w-full", 
    responsive ? "overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" : "overflow-auto"
  )}>
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm table-auto", className)}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & { 
    priority?: "essential" | "key" | "optional" | "actions";
    hideOnMobile?: boolean;
  }
>(({ className, priority, hideOnMobile, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-2 sm:px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 whitespace-nowrap",
      // Column priority visibility
      priority === "essential" && "table-cell",
      priority === "key" && "hidden sm:table-cell",
      priority === "optional" && "hidden md:table-cell",
      priority === "actions" && "table-cell sticky right-0 bg-white dark:bg-slate-900 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]",
      hideOnMobile && "hidden md:table-cell",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & { 
    nowrap?: boolean;
    priority?: "essential" | "key" | "optional" | "actions";
    hideOnMobile?: boolean;
    truncate?: boolean;
  }
>(({ className, nowrap, priority, hideOnMobile, truncate, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 sm:p-4 align-middle [&:has([role=checkbox])]:pr-0", 
      nowrap ? "whitespace-nowrap" : "overflow-hidden",
      truncate && "truncate max-w-0",
      // Column priority visibility  
      priority === "essential" && "table-cell",
      priority === "key" && "hidden sm:table-cell",
      priority === "optional" && "hidden md:table-cell", 
      priority === "actions" && "table-cell sticky right-0 bg-white dark:bg-slate-900 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]",
      hideOnMobile && "hidden md:table-cell",
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

// Mobile Card Components for responsive table fallback
interface MobileCardProps {
  children: React.ReactNode;
  className?: string;
}

const MobileCard = React.forwardRef<HTMLDivElement, MobileCardProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "md:hidden p-4 border rounded-lg bg-white dark:bg-slate-900 space-y-3 mb-4",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)
MobileCard.displayName = "MobileCard"

interface MobileCardRowProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

const MobileCardRow = React.forwardRef<HTMLDivElement, MobileCardRowProps>(
  ({ label, children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex justify-between items-start gap-3", className)}
      {...props}
    >
      <span className="text-sm font-medium text-muted-foreground min-w-0 flex-shrink-0">
        {label}:
      </span>
      <div className="text-sm text-right min-w-0 flex-1 break-words">
        {children}
      </div>
    </div>
  )
)
MobileCardRow.displayName = "MobileCardRow"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  MobileCard,
  MobileCardRow,
}
